import { useMemo, useState } from "react";

import { useToast } from "@smart/bridge-intake-components-dom";
import { getMatterTypeRepresentatives } from "@smart/bridge-smokeball-basic";
import { useAsync } from "@smart/itops-hooks-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { entriesOf, fromEntries, groupBy } from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  useLazyLoadRelationships,
  useLoadFields,
  useLoadForm,
  useLoadGroups,
  useLoadSections,
  useQuerySubmission,
  useUpdateResponseExistingItemIds,
  useUpdateResponseSyncStatus,
} from "@smart/manage-gql-client-dom";
import { useSyncToMatter } from "@smart/manage-hooks-dom";

import { ContactReview } from "../view/contact-review";

type ReviewContactProps = {
  onClose: () => void;
  matterSubmission: MatterSubmission | undefined;
};

export const ReviewContactModal = ({
  onClose,
  matterSubmission,
}: ReviewContactProps) => {
  const { setToasts } = useToast();
  const { matters, roles, contacts, relationships, layouts, hostType } =
    useSmokeballApp();
  const [updateResponseExistingItemIds] = useUpdateResponseExistingItemIds();
  const [updateResponseSyncStatus] = useUpdateResponseSyncStatus();
  const querySubmission = useQuerySubmission("network-only");
  const queryRelationships = useLazyLoadRelationships();
  const { sync, isSyncing } = useSyncToMatter();

  const [rolesAndRelationships, setRolesAndRelationships] = useState<
    Record<string, { contactId: string }[]>
  >({});

  const submissionUri = matterSubmission?.uri;
  const skip = !matterSubmission?.formUri || !submissionUri;
  const form = useLoadForm(
    { uri: matterSubmission?.formUri || "", submissionUri },
    { skip },
  );
  const sections = useLoadSections(
    { formUri: matterSubmission?.formUri || "", submissionUri },
    { skip },
  );
  const groups = useLoadGroups(
    { formUri: matterSubmission?.formUri || "", submissionUri },
    { skip },
  );
  const fields = useLoadFields(
    { formUri: matterSubmission?.formUri || "", submissionUri },
    { skip },
  );

  const formMatterTypeRepresentatives = useMemo(
    () =>
      form.result
        ? getMatterTypeRepresentatives(form.result.values.matterTypes)
        : undefined,
    [form.result],
  );

  useAsync(async () => {
    // web queries relationships by matterId
    // firing before matters.current loads yields per-role errors (swallowed to [])
    if (hostType === "web" && !matters?.current?.id) return;

    const existingRoles = await roles?.get();
    const existingRelationships = await Promise.all(
      existingRoles?.roles?.map(async (role) => {
        let roleRelationships;
        try {
          if (hostType === "web") {
            // Using Smokeball API in as the Web SDK doesn't support this call yet.
            const queryResult = await queryRelationships({
              matterId: matters?.current?.id || "",
              roleId: role.id,
            });
            roleRelationships = queryResult?.map((r) => ({
              id: r.id,
              contactId: r.contactId || "",
              name: r.name || "",
              representativeIds: r.representativeIds || [],
            }));
          } else {
            roleRelationships = (await relationships?.get(role.id))
              ?.relationships;
          }
          return {
            roleId: role.id,
            relationships: roleRelationships,
          };
        } catch (error) {
          console.error("Error getting relationships for role id: ", role.id);
          return {
            roleId: role.id,
            relationships: [],
          };
        }
      }) || [],
    );
    const rolesMap = groupBy(existingRoles?.roles || [], (r) => r.name);
    const relationshipsMap = entriesOf(rolesMap).reduce<
      Record<string, { contactId: string }[]>
    >((map, [roleName, rolesWithTheSameName]) => {
      const found = existingRelationships.find(
        (rs) => rs.roleId === rolesWithTheSameName[0].id,
      );
      const grouped = groupBy(found?.relationships || [], (rs) => rs.name);

      return {
        ...map,
        ...fromEntries(
          entriesOf(grouped).map(([relationshipName, groupedRelationships]) => [
            `${roleName}/${relationshipName}`,
            groupedRelationships,
          ]),
        ),
      };
    }, {});

    setRolesAndRelationships({
      ...rolesMap,
      ...relationshipsMap,
    });
  }, [roles, relationships, matters?.current?.id, hostType]);

  return matterSubmission ? (
    <ContactReview
      loading={
        form.loading ||
        fields.loading ||
        groups.loading ||
        sections.loading ||
        isSyncing
      }
      matter={matters?.current}
      rolesAndRelationships={rolesAndRelationships}
      onClose={onClose}
      submission={matterSubmission}
      sections={sections.result}
      groups={groups.result}
      fields={fields.result}
      formMatterTypeRepresentatives={formMatterTypeRepresentatives}
      getContact={contacts?.get}
      getRoles={roles?.get}
      getLayoutContacts={layouts?.getLayoutContactsUsingRoleAndRelationship}
      layoutItems={layouts?.current}
      updateResponseExistingItemIds={updateResponseExistingItemIds}
      updateResponseSyncStatus={updateResponseSyncStatus}
      querySubmission={querySubmission}
      sync={sync}
      onSuccess={() => {
        setToasts([{ text: "Contacts successfully saved" }]);
        onClose();
      }}
      onFailure={() => {
        setToasts([
          {
            text: "Failed to save contacts",
            palette: "destructive",
          },
        ]);
        onClose();
      }}
      openContact={contacts?.open}
    />
  ) : null;
};
